import React, { useState, useEffect } from 'react';
import chemicalService from '../../../services/chemicalService';
import {
  generateSalesReportPdf,
  generatePurchaseReportPdf,
  generateStockReportPdf,
  generateProductionReportPdf,
  generateLedgerReportPdf,
  generateProfitLossReportPdf,
} from '../components/reportPdfs';

const REPORT_TYPES = [
  { id: 'sales', label: 'Sales Report' },
  { id: 'purchase', label: 'Purchase Report' },
  { id: 'stock', label: 'Stock Valuation' },
  { id: 'production', label: 'Production Report' },
  { id: 'ledger', label: 'Ledger Statement' },
  { id: 'pnl', label: 'Profit & Loss' },
];

const ReportsTab = () => {
  const [reportType, setReportType] = useState('sales');

  // Filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('all');
  const [productType, setProductType] = useState('all');
  const [partyType, setPartyType] = useState('customer');
  const [partyId, setPartyId] = useState('');

  // Dropdown data
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);

  // Results
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [cRes, vRes] = await Promise.all([
          chemicalService.getCustomers({ limit: 500 }),
          chemicalService.getVendors({ limit: 500 }),
        ]);
        setCustomers(cRes.data || []);
        setVendors(vRes.data || []);
      } catch (e) { console.warn('Could not load parties:', e.message); }
    };
    load();
  }, []);

  // Reset results when report type changes
  useEffect(() => {
    setReport(null);
    setHasRun(false);
  }, [reportType]);

  const runReport = async () => {
    try {
      setLoading(true);
      let res;
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      if (reportType === 'sales') {
        if (customerId) params.customer_id = customerId;
        if (paymentStatus !== 'all') params.payment_status = paymentStatus;
        res = await chemicalService.getSalesReport(params);
      } else if (reportType === 'purchase') {
        if (vendorId) params.vendor_id = vendorId;
        if (paymentStatus !== 'all') params.payment_status = paymentStatus;
        res = await chemicalService.getPurchaseReport(params);
      } else if (reportType === 'stock') {
        if (productType !== 'all') params.product_type = productType;
        res = await chemicalService.getStockReport(params);
      } else if (reportType === 'production') {
        res = await chemicalService.getProductionReport(params);
      } else if (reportType === 'ledger') {
        if (!partyId) { alert('⚠️ Please select a party'); setLoading(false); return; }
        params.party_type = partyType;
        params.party_id = partyId;
        res = await chemicalService.getLedgerReport(params);
      } else if (reportType === 'pnl') {
        res = await chemicalService.getProfitLossReport(params);
      }

      setReport(res);
      setHasRun(true);
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to generate report'));
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = () => {
    if (!report) { alert('⚠️ Run a report first'); return; }
    const f = { from_date: fromDate, to_date: toDate, payment_status: paymentStatus };
    if (customerId) f.customer_name = customers.find((c) => String(c.id) === String(customerId))?.name;
    if (vendorId) f.vendor_name = vendors.find((v) => String(v.id) === String(vendorId))?.name;

    if (reportType === 'sales') generateSalesReportPdf(report, f);
    else if (reportType === 'purchase') generatePurchaseReportPdf(report, f);
    else if (reportType === 'stock') generateStockReportPdf(report, f);
    else if (reportType === 'production') generateProductionReportPdf(report, f);
    else if (reportType === 'ledger') generateLedgerReportPdf(report, { ...f, party_type: partyType });
    else if (reportType === 'pnl') generateProfitLossReportPdf(report, f);
  };

  const fmtMoney = (n) => 'Rs ' + Math.round(parseFloat(n) || 0).toLocaleString('en-PK');
  const fmtNum = (n) => { const v = parseFloat(n) || 0; return v % 1 === 0 ? v.toLocaleString('en-PK') : v.toFixed(2); };
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
  const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '—');

  const hasResults = report && (
    (report.rows && report.rows.length > 0) || (reportType === 'pnl' && report.summary)
  );

  return (
    <div style={{ padding: '24px 30px 40px' }}>

      {/* Report type */}
      <div style={{ marginBottom: '16px' }}>
        <label style={styles.label}>Report Type</label>
        <select value={reportType} onChange={(e) => setReportType(e.target.value)} style={styles.typeSelect}>
          {REPORT_TYPES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
        </select>
      </div>

      {/* Filters */}
      <div style={styles.filterCard}>
        <div style={styles.filterGrid}>
          {/* Date range — shown for all except stock */}
          {reportType !== 'stock' && (
            <>
              <div>
                <label style={styles.label}>From Date</label>
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={styles.input} />
              </div>
              <div>
                <label style={styles.label}>To Date</label>
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={styles.input} />
              </div>
            </>
          )}

          {/* Sales: customer + status */}
          {reportType === 'sales' && (
            <>
              <div>
                <label style={styles.label}>Customer</label>
                <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} style={styles.input}>
                  <option value="">All customers</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={styles.label}>Payment Status</label>
                <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} style={styles.input}>
                  <option value="all">All</option><option value="paid">Paid</option>
                  <option value="partial">Partial</option><option value="unpaid">Unpaid</option>
                </select>
              </div>
            </>
          )}

          {/* Purchase: vendor + status */}
          {reportType === 'purchase' && (
            <>
              <div>
                <label style={styles.label}>Vendor</label>
                <select value={vendorId} onChange={(e) => setVendorId(e.target.value)} style={styles.input}>
                  <option value="">All vendors</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <label style={styles.label}>Payment Status</label>
                <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} style={styles.input}>
                  <option value="all">All</option><option value="paid">Paid</option>
                  <option value="partial">Partial</option><option value="unpaid">Unpaid</option>
                </select>
              </div>
            </>
          )}

          {/* Stock: product type */}
          {reportType === 'stock' && (
            <div>
              <label style={styles.label}>Product Type</label>
              <select value={productType} onChange={(e) => setProductType(e.target.value)} style={styles.input}>
                <option value="all">All types</option>
                <option value="raw">Raw</option>
                <option value="finished">Finished</option>
                <option value="trading">Trading</option>
              </select>
            </div>
          )}

          {/* Ledger: party type + party */}
          {reportType === 'ledger' && (
            <>
              <div>
                <label style={styles.label}>Party Type</label>
                <select value={partyType} onChange={(e) => { setPartyType(e.target.value); setPartyId(''); }} style={styles.input}>
                  <option value="customer">Customer</option>
                  <option value="vendor">Vendor</option>
                </select>
              </div>
              <div>
                <label style={styles.label}>{partyType === 'customer' ? 'Customer' : 'Vendor'}</label>
                <select value={partyId} onChange={(e) => setPartyId(e.target.value)} style={styles.input}>
                  <option value="">-- Select --</option>
                  {(partyType === 'customer' ? customers : vendors).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>

        <div style={styles.filterActions}>
          <button onClick={runReport} style={styles.runButton} disabled={loading}>
            {loading ? '⏳ Generating...' : '📊 Generate Report'}
          </button>
          <button onClick={downloadPdf} style={styles.pdfButton} disabled={!hasResults}>
            📄 Download PDF
          </button>
        </div>
      </div>

      {/* Results */}
      {hasRun && !hasResults && reportType !== 'pnl' && (
        <div style={styles.placeholder}>No data found for the selected filters</div>
      )}

      {hasRun && hasResults && (
        <ResultsView
          reportType={reportType} report={report}
          fmtMoney={fmtMoney} fmtNum={fmtNum} fmtDate={fmtDate} titleCase={titleCase}
        />
      )}

      {!hasRun && (
        <div style={styles.placeholder}>
          <div style={{ fontSize: '40px', marginBottom: '10px' }}>📊</div>
          Select filters and click "Generate Report"
        </div>
      )}
    </div>
  );
};

// ─── Results rendering per report type ───────────────────────
const ResultsView = ({ reportType, report, fmtMoney, fmtNum, fmtDate, titleCase }) => {
  const t = report.totals || {};

  if (reportType === 'pnl') {
    const s = report.summary || {};
    const profit = s.net_profit >= 0;
    return (
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <tbody>
            <tr style={styles.row}><td style={styles.td}>Sales Revenue</td><td style={{ ...styles.td, textAlign: 'right', color: '#27ae60', fontWeight: '700' }}>{fmtMoney(s.sales_revenue)}</td></tr>
            <tr style={styles.row}><td style={styles.td}>Less: Purchase Cost</td><td style={{ ...styles.td, textAlign: 'right', color: '#e74c3c' }}>({fmtMoney(s.purchase_cost)})</td></tr>
            <tr style={styles.row}><td style={styles.td}>Less: Total Expenses</td><td style={{ ...styles.td, textAlign: 'right', color: '#e74c3c' }}>({fmtMoney(s.total_expenses)})</td></tr>
            <tr style={{ background: profit ? '#d4edda' : '#f8d7da' }}>
              <td style={{ ...styles.td, fontWeight: '700', fontSize: '16px' }}>NET {profit ? 'PROFIT' : 'LOSS'}</td>
              <td style={{ ...styles.td, textAlign: 'right', fontWeight: '700', fontSize: '16px', color: profit ? '#155724' : '#721c24' }}>{fmtMoney(s.net_profit)}</td>
            </tr>
          </tbody>
        </table>
        {report.expense_breakdown && report.expense_breakdown.length > 0 && (
          <div style={{ padding: '20px' }}>
            <div style={{ fontWeight: '700', color: '#2c3e50', marginBottom: '10px' }}>Expense Breakdown</div>
            <table style={styles.table}>
              <tbody>
                {report.expense_breakdown.map((e, i) => (
                  <tr key={i} style={styles.row}>
                    <td style={styles.td}>{e.category}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{fmtMoney(e.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // Table-based reports
  let head = [], bodyRows = [], foot = null;

  if (reportType === 'sales') {
    head = ['Invoice', 'Date', 'Customer', 'Items', 'Subtotal', 'GST', 'Total', 'Paid', 'Balance', 'Status'];
    bodyRows = report.rows.map((r) => [r.invoice_no, fmtDate(r.invoice_date), r.customer_name, r.items_summary || '—', fmtMoney(r.subtotal), fmtMoney(r.gst_amount), fmtMoney(r.grand_total), fmtMoney(r.amount_paid), fmtMoney(r.balance), titleCase(r.payment_status)]);
    foot = `${t.invoice_count} invoices · Total ${fmtMoney(t.total_sales)} · Paid ${fmtMoney(t.total_paid)} · Outstanding ${fmtMoney(t.total_outstanding)}`;
  } else if (reportType === 'purchase') {
    head = ['Bill', 'Date', 'Vendor', 'Items', 'Subtotal', 'GST', 'Total', 'Paid', 'Balance', 'Status'];
    bodyRows = report.rows.map((r) => [r.bill_no, fmtDate(r.purchase_date), r.vendor_name, r.items_summary || '—', fmtMoney(r.subtotal), fmtMoney(r.gst_amount), fmtMoney(r.grand_total), fmtMoney(r.amount_paid), fmtMoney(r.balance), titleCase(r.payment_status)]);
    foot = `${t.bill_count} bills · Total ${fmtMoney(t.total_purchases)} · Paid ${fmtMoney(t.total_paid)} · Outstanding ${fmtMoney(t.total_outstanding)}`;
  } else if (reportType === 'stock') {
    head = ['Product', 'Code', 'Type', 'Batch', 'Qty', 'Cost/Unit', 'Value', 'Expiry'];
    bodyRows = report.rows.map((r) => [r.product_name, r.product_code, titleCase(r.product_type), r.batch_no, `${fmtNum(r.qty_available)} ${r.product_unit}`, fmtMoney(r.cost_per_unit), fmtMoney(r.stock_value), fmtDate(r.expiry_date)]);
    foot = `${t.batch_count} batches · Total stock value ${fmtMoney(t.total_value)}`;
  } else if (reportType === 'production') {
    head = ['Batch', 'Date', 'Product', 'Recipe', 'Mult', 'Expected', 'Actual', 'Cost'];
    bodyRows = report.rows.map((r) => [r.batch_no, fmtDate(r.production_date), r.product_name, r.formula_code, `${parseFloat(r.multiplier).toFixed(1)}x`, `${fmtNum(r.expected_output)} ${r.product_unit}`, `${fmtNum(r.actual_output)} ${r.product_unit}`, fmtMoney(r.total_cost)]);
    foot = `${t.production_count} runs · Total output ${fmtNum(t.total_output)} · Total cost ${fmtMoney(t.total_cost)}`;
  } else if (reportType === 'ledger') {
    head = ['Date', 'Reference', 'Type', 'Charge', 'Paid', 'Balance'];
    bodyRows = report.rows.map((r) => [fmtDate(r.date), r.against_no || r.ref, titleCase(r.kind), r.charge ? fmtMoney(r.charge) : '—', r.paid ? fmtMoney(r.paid) : '—', fmtMoney(r.balance)]);
    foot = `${report.party?.name || ''} · Charges ${fmtMoney(t.total_charge)} · Paid ${fmtMoney(t.total_paid)} · Closing balance ${fmtMoney(t.closing_balance)}`;
  }

  return (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr style={styles.headerRow}>
            {head.map((h, i) => <th key={i} style={{ ...styles.th, textAlign: i >= 4 && reportType !== 'ledger' ? 'right' : 'left' }}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {bodyRows.map((row, ri) => {
            const isPaymentRow = reportType === 'ledger' && (row[2] === 'Payment' || row[2] === 'Receipt');
            return (
              <tr key={ri} style={{ ...styles.row, ...(isPaymentRow ? styles.paymentRow : {}) }}>
                {row.map((cell, ci) => (
                  <td key={ci} style={{ ...styles.td, textAlign: ci >= 4 && reportType !== 'ledger' ? 'right' : (ci >= 3 && reportType === 'ledger' ? 'right' : 'left') }}>{cell}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {foot && <div style={styles.footBar}>{foot}</div>}
    </div>
  );
};

const styles = {
  label: { display: 'block', fontSize: '12px', fontWeight: '700', color: '#7f8c8d', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.3px' },
  typeSelect: { padding: '10px 14px', border: '2px solid #e74c3c', borderRadius: '8px', fontSize: '15px', fontWeight: '600', color: '#2c3e50', fontFamily: 'inherit', minWidth: '260px' },
  filterCard: { background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '20px' },
  filterGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '16px' },
  input: { width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box' },
  filterActions: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  runButton: { padding: '12px 24px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '700' },
  pdfButton: { padding: '12px 24px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '700' },
  tableWrap: { background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  headerRow: { background: '#f8f9fa' },
  th: { padding: '14px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#2c3e50', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap' },
  row: { borderBottom: '1px solid #f0f0f0' },
  paymentRow: { background: '#429a68' },
  td: { padding: '12px 14px', fontSize: '13px', color: '#2c3e50' },
  footBar: { padding: '14px 16px', background: '#ecf0f1', fontSize: '13px', fontWeight: '700', color: '#2c3e50', borderTop: '2px solid #d0d0d0' },
  placeholder: { background: 'white', padding: '60px', borderRadius: '12px', textAlign: 'center', color: '#95a5a6', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
};

export default ReportsTab;